/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faBell,
  faEye,
  faShieldAlt,
  faGlobe,
  faPalette,
  faSave,
  faCheck,
  faTimes,
  faEdit,
  faTrash,
  faKey,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBriefcase,
  faToggleOn,
  faToggleOff,
  faCheckCircle,
  faExclamationCircle,
  faUserGraduate,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import AccountDeletionModal from './AccountDeletionModal';
import './SettingsPage.css';

const SettingsPage = ({ showConvertToJobSeeker = false }) => {
  const { user, updateUser, logout, loginWithUserData } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [converting, setConverting] = useState(false);
  
  // Profile Settings State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    currentJobTitle: '',
    bio: ''
  });

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    jobAlerts: true,
    applicationUpdates: true,
    messageNotifications: true,
    weeklyDigest: false,
    marketingEmails: false
  });

  // Privacy Settings State
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    searchableByRecruiters: true,
    showLastActive: true
  });

  // Display Preferences State
  const [displaySettings, setDisplaySettings] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'UTC'
  });

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch user profile
      const profileResponse = await axios.get(buildApiUrl('/api/user/profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (profileResponse.data) {
        setProfileData({
          firstName: profileResponse.data.firstName || '',
          lastName: profileResponse.data.lastName || '',
          email: profileResponse.data.email || '',
          phone: profileResponse.data.phone || '',
          location: profileResponse.data.currentCity || '',
          currentJobTitle: profileResponse.data.professionalTitle || profileResponse.data.currentJobTitle || '',
          bio: profileResponse.data.professionalSummary || ''
        });
        
        // Check for 2FA status in user profile
        if (profileResponse.data.twoFactorEnabled !== undefined) {
          setTwoFactorEnabled(profileResponse.data.twoFactorEnabled);
        }
      }

      // Fetch user settings if endpoint exists
      try {
        const settingsResponse = await axios.get(buildApiUrl('/api/user/settings'), {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (settingsResponse.data) {
          setNotificationSettings(settingsResponse.data.notifications || notificationSettings);
          setPrivacySettings(settingsResponse.data.privacy || privacySettings);
          setDisplaySettings(settingsResponse.data.display || displaySettings);
        }
      } catch (error) {
        console.log('Settings endpoint not available, using defaults');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setSaveMessage({ type, text });
    setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        buildApiUrl('/api/profile/update'),
        {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
          currentCity: profileData.location,
          professionalTitle: profileData.currentJobTitle,
          professionalSummary: profileData.bio
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        showMessage('success', 'Profile updated successfully!');
        if (updateUser) {
          updateUser(response.data);
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage('error', 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        buildApiUrl('/api/auth/change-password'),
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
      showMessage('success', 'Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showMessage('error', error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const newState = !twoFactorEnabled;
      const response = await axios.post(
        buildApiUrl('/api/auth/toggle-2fa'),
        { enabled: newState },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setTwoFactorEnabled(newState);
        showMessage('success', response.data?.message || `2FA ${newState ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      showMessage('error', error.response?.data?.error || 'Failed to toggle 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        buildApiUrl('/api/user/settings'),
        { notifications: notificationSettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage('success', 'Notification settings updated!');
    } catch (error) {
      console.error('Error updating notifications:', error);
      showMessage('success', 'Notification preferences saved!');
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyUpdate = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        buildApiUrl('/api/user/settings'),
        { privacy: privacySettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage('success', 'Privacy settings updated!');
    } catch (error) {
      console.error('Error updating privacy:', error);
      showMessage('success', 'Privacy preferences saved!');
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayUpdate = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        buildApiUrl('/api/user/settings'),
        { display: displaySettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage('success', 'Display settings updated!');
    } catch (error) {
      console.error('Error updating display:', error);
      showMessage('success', 'Display preferences saved!');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToJobSeeker = async () => {
    const confirmed = window.confirm(
      'Switch to Job Seeker Account?\n\n' +
      'Your intern profile data will be carried over. You will be redirected to the Job Seeker dashboard where you can browse and apply for full-time jobs.\n\n' +
      'This change cannot be undone from the app. Continue?'
    );
    if (!confirmed) return;

    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        buildApiUrl('/api/intern/convert-to-jobseeker'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        const convertedUser = response.data.user || {};
        const userData = {
          token,
          userId: convertedUser.userId || user?.userId || localStorage.getItem('userId'),
          email: convertedUser.email || user?.email,
          firstName: convertedUser.firstName || user?.firstName,
          lastName: convertedUser.lastName || user?.lastName,
          phone: convertedUser.phone || user?.phone,
          role: 'jobSeeker',
          userType: 'job_seeker',
          profileCompleted: convertedUser.profileCompleted,
          hasCompletedProfile: convertedUser.hasCompletedProfile,
        };

        localStorage.setItem('role', 'jobSeeker');
        localStorage.setItem('userType', 'job_seeker');
        localStorage.setItem('profileCompleted', String(!!convertedUser.profileCompleted));
        localStorage.setItem('hasCompletedProfile', String(!!convertedUser.hasCompletedProfile));
        localStorage.removeItem('internDashboardActiveSection');
        sessionStorage.removeItem('intendedDestination');

        if (loginWithUserData) {
          loginWithUserData(userData, false);
        } else if (updateUser) {
          updateUser(userData);
        }

        // Full reload ensures job seeker dashboard, profile API, and sidebar load fresh
        window.location.replace('/jobseeker-dashboard');
        return;
      }
    } catch (error) {
      console.error('Error converting to job seeker:', error);
      showMessage('error', error.response?.data?.error || 'Failed to convert account');
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteAccount = async (deletionData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        buildApiUrl('/api/user/settings/delete-account'),
        {
          headers: { Authorization: `Bearer ${token}` },
          data: deletionData
        }
      );

      if (response.status === 200) {
        // Clear all local storage
        localStorage.clear();
        // Logout user
        if (logout) {
          logout();
        }
        // Navigate to home
        navigate('/');
        alert('Your account has been permanently deleted.');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  const tabs = [
    { id: 'account', label: 'Account Security', icon: faLock }
  ];

  return (
    <div className="settings-page-container" style={{ padding: '0', margin: '0' }}>
      <div className="settings-layout" style={{ marginTop: '0', paddingTop: '0' }}>
        {/* Tab Content */}
        <div className="settings-content" style={{ marginTop: '0', paddingTop: '0' }}>
          {saveMessage.text && (
            <div className={`save-message ${saveMessage.type}`}>
              <FontAwesomeIcon icon={saveMessage.type === 'success' ? faCheckCircle : faExclamationCircle} />
              {saveMessage.text}
            </div>
          )}

          {/* Account Security */}
          {activeTab === 'account' && (
            <div className="settings-section" style={{ marginTop: '0', paddingTop: '0' }}>
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <div className="input-with-icon">
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <div className="input-with-icon">
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="Enter new password"
                    />
                  </div>
                  <small className="help-text">Password must be at least 6 characters long</small>
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <div className="input-with-icon">
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    <FontAwesomeIcon icon={faKey} />
                    {loading ? 'Updating...' : 'Change Password'}
                  </button>
                </div>
              </form>

              <div className="two-factor-section">
                <h3 className="two-factor-title">
                  <FontAwesomeIcon icon={faShieldAlt} /> Two-Factor Authentication
                </h3>
                <p className="two-factor-description">
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>
                <div className="two-factor-row">
                  <div className="two-factor-status">
                    <div className="two-factor-status-title">
                      {twoFactorEnabled ? '2FA is Enabled' : '2FA is Disabled'}
                    </div>
                    <div className="two-factor-status-text">
                      {twoFactorEnabled 
                        ? 'Your account is protected with two-factor authentication' 
                        : 'Enable 2FA to secure your account'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`two-factor-action-btn ${twoFactorEnabled ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={handleToggle2FA}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={twoFactorEnabled ? faToggleOn : faToggleOff} />
                    {loading ? 'Processing...' : (twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA')}
                  </button>
                </div>
              </div>

              <div className="security-info">
                <h3>Security Tips</h3>
                <ul>
                  <li>Use a strong, unique password</li>
                  <li>Don't share your password with anyone</li>
                  <li>Change your password regularly</li>
                  <li>Enable two-factor authentication when available</li>
                </ul>
              </div>

              {showConvertToJobSeeker && (
                <div
                  className="convert-jobseeker-section"
                  style={{
                    marginTop: '3rem',
                    paddingTop: '2rem',
                    borderTop: '2px solid #e5e7eb'
                  }}
                >
                  <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0d9488' }}>
                    <FontAwesomeIcon icon={faBriefcase} /> Switch to Job Seeker
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                    Finished your internship journey or ready for full-time roles? Convert your account to a Job Seeker profile.
                    Your existing profile details will be migrated so you can start browsing jobs, saving listings, and applying right away.
                  </p>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleConvertToJobSeeker}
                    disabled={loading || converting}
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: (loading || converting) ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FontAwesomeIcon icon={converting ? faCheck : faUserGraduate} />
                    {converting ? 'Converting...' : 'Convert to Job Seeker'}
                    {!converting && <FontAwesomeIcon icon={faArrowRight} />}
                  </button>
                </div>
              )}

              <div className="account-deletion-section">
                <h3 className="account-deletion-title">
                  <FontAwesomeIcon icon={faTrash} /> Danger Zone
                </h3>
                <p className="account-deletion-description">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  type="button"
                  className="btn-danger account-deletion-btn"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete Account
                </button>
              </div>
            </div>
          )}

          {/* Notifications - Removed */}
          {false && activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>
                  <FontAwesomeIcon icon={faBell} /> Notification Preferences
                </h2>
                <p>Choose what notifications you want to receive</p>
              </div>

              <div className="toggle-list">
                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Email Notifications</h4>
                    <p>Receive email notifications for important updates</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.emailNotifications ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: !notificationSettings.emailNotifications
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.emailNotifications ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Job Alerts</h4>
                    <p>Get notified about new job postings matching your profile</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.jobAlerts ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      jobAlerts: !notificationSettings.jobAlerts
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.jobAlerts ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Application Updates</h4>
                    <p>Receive updates about your job applications</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.applicationUpdates ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      applicationUpdates: !notificationSettings.applicationUpdates
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.applicationUpdates ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Message Notifications</h4>
                    <p>Be notified when you receive new messages</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.messageNotifications ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      messageNotifications: !notificationSettings.messageNotifications
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.messageNotifications ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Weekly Digest</h4>
                    <p>Receive a weekly summary of your activity</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.weeklyDigest ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      weeklyDigest: !notificationSettings.weeklyDigest
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.weeklyDigest ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Marketing Emails</h4>
                    <p>Receive promotional content and offers</p>
                  </div>
                  <button
                    className={`toggle-btn ${notificationSettings.marketingEmails ? 'active' : ''}`}
                    onClick={() => setNotificationSettings({
                      ...notificationSettings,
                      marketingEmails: !notificationSettings.marketingEmails
                    })}
                  >
                    <FontAwesomeIcon icon={notificationSettings.marketingEmails ? faToggleOn : faToggleOff} />
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn-primary" onClick={handleNotificationUpdate} disabled={loading}>
                  <FontAwesomeIcon icon={faSave} />
                  {loading ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Privacy Settings - Removed */}
          {false && activeTab === 'privacy' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>
                  <FontAwesomeIcon icon={faShieldAlt} /> Privacy Settings
                </h2>
                <p>Control who can see your information</p>
              </div>

              <div className="form-group">
                <label>Profile Visibility</label>
                <select
                  value={privacySettings.profileVisibility}
                  onChange={(e) => setPrivacySettings({ ...privacySettings, profileVisibility: e.target.value })}
                  className="settings-select"
                >
                  <option value="public">Public - Anyone can view your profile</option>
                  <option value="private">Private - Only you can view your profile</option>
                  <option value="recruiters">Recruiters Only - Only recruiters can view</option>
                </select>
              </div>

              <div className="toggle-list">
                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Show Email Address</h4>
                    <p>Display your email on your public profile</p>
                  </div>
                  <button
                    className={`toggle-btn ${privacySettings.showEmail ? 'active' : ''}`}
                    onClick={() => setPrivacySettings({
                      ...privacySettings,
                      showEmail: !privacySettings.showEmail
                    })}
                  >
                    <FontAwesomeIcon icon={privacySettings.showEmail ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Show Phone Number</h4>
                    <p>Display your phone number on your public profile</p>
                  </div>
                  <button
                    className={`toggle-btn ${privacySettings.showPhone ? 'active' : ''}`}
                    onClick={() => setPrivacySettings({
                      ...privacySettings,
                      showPhone: !privacySettings.showPhone
                    })}
                  >
                    <FontAwesomeIcon icon={privacySettings.showPhone ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Searchable by Recruiters</h4>
                    <p>Allow recruiters to find your profile in searches</p>
                  </div>
                  <button
                    className={`toggle-btn ${privacySettings.searchableByRecruiters ? 'active' : ''}`}
                    onClick={() => setPrivacySettings({
                      ...privacySettings,
                      searchableByRecruiters: !privacySettings.searchableByRecruiters
                    })}
                  >
                    <FontAwesomeIcon icon={privacySettings.searchableByRecruiters ? faToggleOn : faToggleOff} />
                  </button>
                </div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <h4>Show Last Active Status</h4>
                    <p>Let others see when you were last active</p>
                  </div>
                  <button
                    className={`toggle-btn ${privacySettings.showLastActive ? 'active' : ''}`}
                    onClick={() => setPrivacySettings({
                      ...privacySettings,
                      showLastActive: !privacySettings.showLastActive
                    })}
                  >
                    <FontAwesomeIcon icon={privacySettings.showLastActive ? faToggleOn : faToggleOff} />
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn-primary" onClick={handlePrivacyUpdate} disabled={loading}>
                  <FontAwesomeIcon icon={faSave} />
                  {loading ? 'Saving...' : 'Save Privacy Settings'}
                </button>
              </div>
            </div>
          )}

          {/* Display Settings - Removed */}
          {false && activeTab === 'display' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>
                  <FontAwesomeIcon icon={faPalette} /> Display Preferences
                </h2>
                <p>Customize how the platform looks and feels</p>
              </div>

              <div className="form-group">
                <label>Theme</label>
                <select
                  value={displaySettings.theme}
                  onChange={(e) => setDisplaySettings({ ...displaySettings, theme: e.target.value })}
                  className="settings-select"
                >
                  <option value="light">Light Mode</option>
                  <option value="dark">Dark Mode</option>
                  <option value="auto">Auto (System Preference)</option>
                </select>
                <small className="help-text">Theme preferences will be applied in a future update</small>
              </div>

              <div className="form-group">
                <label>Language</label>
                <select
                  value={displaySettings.language}
                  onChange={(e) => setDisplaySettings({ ...displaySettings, language: e.target.value })}
                  className="settings-select"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>

              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={displaySettings.timezone}
                  onChange={(e) => setDisplaySettings({ ...displaySettings, timezone: e.target.value })}
                  className="settings-select"
                >
                  <option value="UTC">UTC (Coordinated Universal Time)</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                </select>
              </div>

              <div className="form-actions">
                <button className="btn-primary" onClick={handleDisplayUpdate} disabled={loading}>
                  <FontAwesomeIcon icon={faSave} />
                  {loading ? 'Saving...' : 'Save Display Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AccountDeletionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteAccount}
        accountType="jobSeeker"
      />
    </div>
  );
};

export default SettingsPage;

